import MainLayout from '@/components/layout/MainLayout';
import { AdminSupportConversationList } from '@/components/support/AdminSupportConversationList';
import { SupportMessageComposer, type SupportMessageComposerSubmission } from '@/components/support/SupportMessageComposer';

import { SupportMessageList } from '@/components/support/SupportMessageList';
import SEO from '@/components/common/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import LoadingScreen from '@/components/ui/loading-screen';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminSupportConversations } from '@/hooks/useAdminSupportConversations';
import { useProfile } from '@/hooks/useProfile';
import { useSupportConversationPresence } from '@/hooks/useSupportConversationPresence';
import { useSupportMessages } from '@/hooks/useSupportMessages';

import {
  createOptimisticSupportMessage,
  sendAdminSupportReply,
  sortSupportMessages,
  type SupportConversationThread,
  type SupportMessageLike,
} from '@/lib/supportChat';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, ShieldAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Link, useSearchParams } from 'react-router-dom';

function getPresenceBadgeClass(status: 'active' | 'inactive' | 'left') {
  if (status === 'active') {
    return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  }

  if (status === 'inactive') {
    return 'bg-amber-500/10 text-amber-700 dark:text-amber-300';
  }

  return 'bg-muted text-muted-foreground';
}

function getPresenceLabel(status: 'active' | 'inactive' | 'left') {
  if (status === 'active') {
    return 'Active now';
  }

  if (status === 'inactive') {
    return 'Inactive';
  }

  return 'Left site';
}

export default function SupportInbox() {

  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState<SupportMessageLike[]>([]);
  const isAdmin = profile?.role === 'admin';

  const {
    conversations,
    loading: conversationsLoading,
    error: conversationsError,
    refreshConversations,
  } = useAdminSupportConversations(Boolean(user && isAdmin));

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const {
    messages,
    loading: messagesLoading,
    error: messagesError,
    upsertMessage,
    refreshMessages,
  } = useSupportMessages({
    conversationId: selectedConversation?.id,
    enabled: Boolean(user && isAdmin && selectedConversation?.id),
  });

  const displayedMessages = useMemo(
    () => sortSupportMessages([...messages, ...optimisticMessages]),
    [messages, optimisticMessages]
  );

  const selectedConversationPresence = useSupportConversationPresence({
    conversationId: selectedConversation?.id,
    observedUserId: selectedConversation?.user_id,
    enabled: Boolean(user && isAdmin && selectedConversation?.id),
  });

  useEffect(() => {
    const requestedReference = searchParams.get('conversation')?.trim().toUpperCase();
    if (!requestedReference) {
      if (!selectedConversationId && conversations.length > 0) {
        setSelectedConversationId(conversations[0].id);
      }
      return;
    }

    const matchingConversation = conversations.find(
      (conversation) => conversation.site_conversation_ref === requestedReference
    );

    if (matchingConversation && matchingConversation.id !== selectedConversationId) {
      setSelectedConversationId(matchingConversation.id);
      return;
    }

    if (!matchingConversation && !selectedConversationId && conversations.length > 0) {
      setSelectedConversationId(conversations[0].id);
    }
  }, [conversations, searchParams, selectedConversationId]);

  const scrollMessagesToBottom = () => {
    const viewport = messageListRef.current;
    if (!viewport) {
      return;
    }

    viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    scrollMessagesToBottom();
  }, [displayedMessages]);

  const handleSelectConversation = (conversation: SupportConversationThread) => {
    setSelectedConversationId(conversation.id);
    setSearchParams({ conversation: conversation.site_conversation_ref });
    setOptimisticMessages([]);
  };

  const handleReply = async ({ body }: SupportMessageComposerSubmission) => {
    if (!selectedConversation) {
      return false;
    }

    const optimisticMessage = createOptimisticSupportMessage(body, selectedConversation.id, {
      senderType: 'support',
      direction: 'outbound_support',
      deliveryStatus: 'delivered',
    });

    setOptimisticMessages((current) => [...current, optimisticMessage]);
    setSending(true);

    try {
      const nextMessage = await sendAdminSupportReply(selectedConversation.id, body);
      setOptimisticMessages((current) => current.filter((message) => message.id !== optimisticMessage.id));
      upsertMessage(nextMessage);
      return true;
    } catch (error) {
      setOptimisticMessages((current) => current.filter((message) => message.id !== optimisticMessage.id));
      toast({
        title: 'Could not send reply',
        description: error instanceof Error ? error.message : 'Please try again in a moment.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSending(false);
    }
  };

  if (user && !profile) {
    return <LoadingScreen />;
  }

  if (!isAdmin) {
    return (
      <MainLayout>
        <SEO title="Support Inbox" description="Admin support inbox for CanXP AI." />
        <div className="container py-8">
          <Card className="mx-auto max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <ShieldAlert className="h-5 w-5 text-destructive" />
                Admin access required
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>This support inbox is only available to CanXP admins.</p>
              <Button asChild variant="outline">
                <Link to="/dashboard">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to dashboard
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEO title="Support Inbox" description="Manage active CanXP AI support conversations from the admin inbox." />
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="border-b border-border/70 px-6 py-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Support Inbox</h1>
            <p className="text-sm text-muted-foreground">
              WhatsApp is used for admin notifications. Reply to users here in the CanXP inbox.
            </p>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 overflow-hidden gap-0 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="flex min-h-0 flex-col border-b border-border/70 bg-muted/10 lg:border-b-0 lg:border-r lg:border-border/70">
            <div className="border-b border-border/70 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">Active Threads</h2>
                <span className="text-xs text-muted-foreground">{conversations.length}</span>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <AdminSupportConversationList
                conversations={conversations}
                selectedConversationId={selectedConversationId}
                loading={conversationsLoading}
                error={conversationsError}
                onRetry={() => {
                  void refreshConversations();
                }}
                onSelectConversation={handleSelectConversation}
              />
            </div>
          </aside>

          <section className="flex min-h-0 flex-1 flex-col overflow-hidden">

            {selectedConversation ? (
              <>
                <div className="border-b border-border/70 px-5 py-4">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold text-foreground">
                        {selectedConversation.profile?.full_name?.trim() || selectedConversation.profile?.email || 'Unnamed user'}
                      </h2>
                      <Badge variant="secondary" className={getPresenceBadgeClass(selectedConversationPresence.status)}>
                        {getPresenceLabel(selectedConversationPresence.status)}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                      <span>{selectedConversation.profile?.email || 'No email on file'}</span>
                      <span>•</span>
                      <span>Conversation #{selectedConversation.site_conversation_ref}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedConversationPresence.status === 'active'
                        ? 'The user is currently viewing the site and support chat.'
                        : selectedConversationPresence.lastSeenAt
                          ? `Last activity ${formatDistanceToNow(new Date(selectedConversationPresence.lastSeenAt), { addSuffix: true })}.`
                          : 'The user is not currently on the site.'}
                    </p>
                  </div>
                </div>

                <div ref={messageListRef} className="min-h-0 flex-1 overflow-y-auto bg-muted/10 px-4 py-4">

                  <SupportMessageList
                    messages={displayedMessages}
                    loading={messagesLoading}
                    error={messagesError}
                    onRetry={() => {
                      void refreshMessages();
                    }}
                    onMediaLoad={scrollMessagesToBottom}
                    currentUserSenderType="support"
                    statusSenderType={null}
                    currentUserLabel="Support Team"
                    otherUserLabel={selectedConversation.profile?.full_name?.trim() || selectedConversation.profile?.email || 'User'}
                    emptyTitle="No messages in this conversation yet."
                    emptyDescription="User messages will appear here as soon as they contact support."
                  />

                </div>
                <SupportMessageComposer
                  disabled={messagesLoading}
                  sending={sending}
                  onSend={handleReply}
                  placeholder="Reply to the user from the CanXP support inbox…"
                  helperText="This reply stays in the CanXP site inbox for the user."
                  sendLabel="Reply"
                  compact
                />

              </>
            ) : (
              <div className="flex flex-1 items-center justify-center p-6">
                <Card className="w-full max-w-xl border-dashed">
                  <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
                    <p className="font-medium text-foreground">Select a conversation to start replying.</p>
                    <p className="text-sm text-muted-foreground">
                      New support requests arrive here and WhatsApp continues acting as the admin notification layer.
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
